import React, { useState } from "react";
import { isEmpty } from "lodash-es";
import { useHistory } from "react-router";
import { SettingOutlined, UserAddOutlined, MailOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { FetchUserQuery, fetchUsers } from "../../../../../api/accounts/user";
import { useQuery } from "react-query";
import Table from "../../OrganizationUsersIndexPage/UsersTable";
import { Card } from "./styles";
import { useFeatureFlagQuery } from "../../../../../hooks/useFeatureFlagQuery";
import useUser from "../../../../../hooks/reduxContainer/_common/useUser";
import InviteDriver from "./Modal/InviteDriver";
import { useOrganizationContext } from "contextProviders/organization/OrganizationProvider";
import Organization from "lib/organization";

interface UsersTableProps {
  query: FetchUserQuery;
}

const UsersTable: React.FC<UsersTableProps> = ({ query }) => {
  const organizationID = query.organization_id || "";
  const history = useHistory();
  const [isInviteDriverModalActive, setIsInviteDriverModalActive] = useState(false);
  const organizationDetails = useOrganizationContext();
  const isSecondClosetOrg = Organization.isSecondCloset(organizationDetails.data);

  // Batch selection state
  const [selectedUsers, setSelectedUsers] = useState<React.Key[]>([]);
  const [areAllUsersSelected, setAreAllUsersSelected] = useState(false);
  const [excludedUsers, setExcludedUsers] = useState<React.Key[]>([]);

  const { user } = useUser();
  const enableCreateUser = useFeatureFlagQuery("enable-create-user", {
    user_id: user.id,
    organization_id: organizationID,
  });

  const enableInviteDriverForCurrentUser = useFeatureFlagQuery("enable-invite-driver", {
    user_id: user.id,
  });

  const users = useQuery(["users", query], () => fetchUsers({ ...query, disabled: false }), {
    enabled: !isEmpty(query),
  });

  // Calculate current page users for batch selection
  const currentPageUsers = users.data?.map((user) => user.id) || [];

  const goToUserCreatePage = () => {
    history.push(`/organizations/${organizationID}/users/create`);
  };

  const goToUserRolesPage = () => {
    history.push(`/organizations/${organizationID}/roles`);
  };

  const headerButtons = [
    <Button onClick={goToUserRolesPage} key="user-roles" icon={<SettingOutlined />}>
      Role Management
    </Button>,
    enableCreateUser.data?.state && (
      <Button className="add-user-btn" onClick={goToUserCreatePage} key="add-user" icon={<UserAddOutlined />}>
        Add User
      </Button>
    ),
    isSecondClosetOrg && enableInviteDriverForCurrentUser.data?.state && (
      <>
        <Button
          className="add-user-btn"
          onClick={() => setIsInviteDriverModalActive(true)}
          key="invite-driver"
          icon={<MailOutlined />}
        >
          Invite Driver
        </Button>
        <InviteDriver
          visible={isInviteDriverModalActive}
          closeModal={() => setIsInviteDriverModalActive(false)}
          organizationID={organizationID}
        />
      </>
    ),
  ];

  return (
    <Card
      title="Users"
      extra={headerButtons}
      actions={[
        <Button key="full-list" type="link" onClick={() => history.push(`/organizations/${organizationID}/users`)}>
          See Full List
        </Button>,
      ]}
    >
      <Table
        page={1}
        pageSize={5}
        isLoading={users.isLoading}
        data={users.data || []}
        organizationID={organizationID}
        onReFetchUsers={users.refetch}
        size="middle"
        pagination={false}
        showActive={true}
        // Batch selection props
        selectedUsers={selectedUsers}
        setSelectedUsers={setSelectedUsers}
        areAllUsersSelected={areAllUsersSelected}
        setAreAllUsersSelected={setAreAllUsersSelected}
        excludedUsers={excludedUsers}
        setExcludedUsers={setExcludedUsers}
        currentPageUsers={currentPageUsers}
        isBatchActionMode={false}
      />
    </Card>
  );
};

export default UsersTable;
